import { useCallback } from 'react';
import { useUnacknowledgedAlarms, useMachineStore, useEStop } from '@/store/store';
import styles from '@/components/AlarmBanner/AlarmBanner.module.css';

/**
 * Full-screen red alarm banner for critical errors (NML_ERROR, OPERATOR_ERROR).
 * Blocks all UI interaction until operator clicks "Acknowledge / Clear Alarm".
 * Industrial standard: operator must physically confirm they've read the error.
 *
 * Also handles the physical E-Stop: while `estop` is engaged the banner shows a
 * dedicated notice with NO close button — it can only be dismissed by releasing
 * the physical button, at which point it clears itself from machine state.
 */
export function AlarmBanner(): JSX.Element | null {
  const alarms = useUnacknowledgedAlarms();
  const estop = useEStop();
  const acknowledgeError = useMachineStore((s) => s.acknowledgeError);

  const handleAcknowledgeAll = useCallback(() => {
    alarms.forEach((alarm) => acknowledgeError(alarm.id));
  }, [alarms, acknowledgeError]);

  if (!estop && alarms.length === 0) return null;

  return (
    <div
      className={styles.alarmOverlay}
      role="alertdialog"
      aria-modal="true"
      aria-label="Machine Alarm"
    >
      <div className={styles.alarmModal}>
        {/* Flashing warning header */}
        <div className={styles.alarmModalHeader}>
          <span className={styles.alarmModalIcon}>⚠️</span>
          <h2 className={styles.alarmModalTitle}>{estop ? 'EMERGENCY STOP' : 'MACHINE ALARM'}</h2>
          <span className={styles.alarmModalIcon}>⚠️</span>
        </div>

        {estop ? (
          /* E-Stop notice: no close button, self-clears when the physical button is released */
          <>
            <div className={styles.alarmModalErrors}>
              <div className={styles.alarmModalError}>
                <span className={styles.alarmModalErrorKind}>🛑 E-STOP</span>
                <p className={styles.alarmModalErrorMessage}>Physical E-Stop button is pressed.</p>
              </div>
            </div>
            <p className={styles.alarmModalWarning}>
              Release the physical E-Stop button to continue.
            </p>
          </>
        ) : (
          <>
            {/* Error list */}
            <div className={styles.alarmModalErrors}>
              {alarms.map((alarm) => (
                <div key={alarm.id} className={styles.alarmModalError}>
                  <span className={styles.alarmModalErrorKind}>
                    {alarm.kind === 'NML_ERROR' ? '🔴 CRITICAL' : '🟠 ERROR'}
                  </span>
                  <p className={styles.alarmModalErrorMessage}>{alarm.message}</p>
                  <span className={styles.alarmModalErrorTime}>
                    {new Date(alarm.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>

            {/* ACK buttons */}
            <div className={styles.alarmModalActions}>
              <button
                className={styles.alarmModalAckBtn}
                onClick={handleAcknowledgeAll}
                type="button"
              >
                ✓ ACKNOWLEDGE / CLEAR ALARM
              </button>
            </div>

            <p className={styles.alarmModalWarning}>
              Machine operations are LOCKED until all alarms are acknowledged.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
